import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0 } from "../../lib/format";
import { Modal, Notice, Pager, Stat } from "../../lib/ui";

/*
 * Payment verification – port of verify-payment.php + payment-Ajax.php + POS-action.php
 * (API: /api/payments/pending and /api/payments/action, backend/payments.py)
 * Verify posts Dr cash 1000 / Lipa 1005 / bank 1010, Cr revenue 4000, and Dr COGS 5500 / Cr inventory 1200.
 * Drop returns the stock, deletes the sale lines and the payment. Each action is logged.
 * "Verify all" verifies exactly the payments matching the current filters. Actions need an admin.
 */

const CLASS_TONE = { POS: "warn", Store: "brand", SERVICE: "ok" };
const fmtDate = (d) => (d ? new Date(d.replace(" ", "T")).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "–");

export default function VerifyPayment() {
  const api = useApi();
  const [f, setF] = useState({ page: 1, sort: "payment_id", order: "ASC", search: "", class_filter: "", method_filter: "" });
  const [term, setTerm] = useState("");
  const [data, setData] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind: verify|drop|all, row?}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (q) => {
    try { setData(await api.get("/api/payments/pending", q)); }
    catch (e) { setNotice({ tone: "err", text: `Failed to load: ${e.message}` }); }
  }, [api]);
  useEffect(() => { load(f); }, [load, f]);
  useEffect(() => { const t = setTimeout(() => setF((s) => (s.search === term.trim() ? s : { ...s, search: term.trim(), page: 1 })), 300); return () => clearTimeout(t); }, [term]);

  const sortBy = (col) => setF((s) => ({ ...s, sort: col, order: s.sort === col && s.order === "ASC" ? "DESC" : "ASC", page: 1 }));
  const Th = ({ col, children, num }) => (
    <th className={`sortable ${num ? "num" : ""}`} onClick={() => sortBy(col)} aria-sort={f.sort === col ? (f.order === "ASC" ? "ascending" : "descending") : "none"}>
      {children} {f.sort === col && <i className={`fas ${f.order === "ASC" ? "fa-sort-up" : "fa-sort-down"}`} />}</th>
  );

  const run = async () => {
    const c = confirm;
    setConfirm(null); setBusy(true);
    try {
      const body = c.kind === "all"
        ? { action: "verify_all", search: f.search, class_filter: f.class_filter, method_filter: f.method_filter }
        : { action: c.kind, payment_id: c.row.payment_id };
      const r = await api.post("/api/payments/action", body);
      const errs = r.errors || [];
      setNotice(errs.length
        ? { tone: "warn", text: `${r.message} ${errs.slice(0, 5).join(" · ")}${errs.length > 5 ? ` · and ${errs.length - 5} more` : ""}` }
        : { tone: "ok", text: r.message });
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
    finally { setBusy(false); load(f); }
  };

  const s = data?.stats;
  const filtered = f.search || f.class_filter || f.method_filter;
  const actions = (
    <button type="button" className="btn primary" disabled={!s?.total_pending || busy} onClick={() => setConfirm({ kind: "all" })}>
      <i className="fas fa-check-double" /> Verify all {filtered ? "shown" : "pending"}{s?.total_pending ? ` (${s.total_pending})` : ""}
    </button>
  );

  return (
    <main className="pg">
      <PageHeader slug="verify-payment" actions={actions} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <div className="ui-stats">
        <Stat icon="fa-clock" tone="warn" label="Total pending" value={s ? s.total_pending : "–"} />
        <Stat icon="fa-coins" label="Pending amount" value={s ? n0(s.total_amount) : "–"} unit="TZS" />
        <Stat icon="fa-briefcase" tone="up" label="Service payments" value={s ? s.service_pending : "–"} />
        <Stat icon="fa-credit-card" tone="muted" label="Payment methods" value={s ? s.payment_methods : "–"} />
      </div>
      <section className="panel">
        <div className="toolbar">
          <div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" />
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search method, class, client, date or payment number" aria-label="Search payments" /></div></div>
          <div className="field"><label htmlFor="vp-cls" className="visually-hidden">Class</label>
            <select id="vp-cls" value={f.class_filter} onChange={(e) => setF({ ...f, class_filter: e.target.value, page: 1 })}>
              <option value="">All classes</option><option>POS</option><option>Store</option><option value="SERVICE">Service</option>
            </select></div>
          <div className="field"><label htmlFor="vp-m" className="visually-hidden">Method</label>
            <select id="vp-m" value={f.method_filter} onChange={(e) => setF({ ...f, method_filter: e.target.value, page: 1 })}>
              <option value="">All methods</option>{(data?.methods || []).map((m) => <option key={m}>{m}</option>)}
            </select></div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><Th col="payment_id">#</Th><Th col="payment_date">Date</Th><th>Client</th><Th col="payment_method">Method</Th>
              <Th col="payment_class">Class</Th><Th col="payment_amount" num>Amount</Th><th>Actions</th></tr></thead>
            <tbody>
              {!data && [0, 1, 2, 3].map((i) => <tr key={i}>{[0, 1, 2, 3, 4, 5, 6].map((c) => <td key={c}><div className="skel" /></td>)}</tr>)}
              {data?.rows.map((r) => {
                const svc = r.payment_class === "SERVICE";
                return (
                  <tr key={r.payment_id}>
                    <td className="mono" style={{ color: "var(--muted)" }}>#{r.payment_id}</td>
                    <td>{fmtDate(r.payment_date)}</td>
                    <td style={{ fontWeight: 600, color: "var(--ink)" }}>{r.client_name}</td>
                    <td>{r.payment_method}</td>
                    <td><span className={`pill-tag ${CLASS_TONE[r.payment_class] || ""}`}>{r.payment_class}</span>
                      {svc && <span className="pill-tag" style={{ marginLeft: ".3rem" }}>No tax</span>}</td>
                    <td className="num mono">{n0(r.payment_amount)} <small style={{ opacity: .5 }}>TZS</small></td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button type="button" className="btn sm primary" disabled={busy} onClick={() => setConfirm({ kind: "verify", row: r })}>
                        <i className="fas fa-check" /> Verify{svc ? " (no tax)" : ""}</button>{" "}
                      <button type="button" className="btn sm" disabled={busy} onClick={() => setConfirm({ kind: "drop", row: r })}><i className="fas fa-xmark" /> Drop</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && data.rows.length === 0 && <div className="empty"><i className="fas fa-inbox" />No pending payments. All payments have been verified or none match your filters.</div>}
        </div>
        {data && <Pager page={data.page} totalPages={data.pages} onPage={(p) => { setF({ ...f, page: p }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
      </section>

      {confirm && (
        <Modal title={confirm.kind === "all" ? `Verify ${s.total_pending} payment${s.total_pending === 1 ? "" : "s"}?` : confirm.kind === "verify" ? "Verify payment?" : "Drop payment?"}
          onClose={() => setConfirm(null)}
          actions={<><button type="button" className="btn" onClick={() => setConfirm(null)}>Cancel</button>
            <button type="button" className={`btn ${confirm.kind === "drop" ? "danger" : "primary"}`} onClick={run}>
              {confirm.kind === "all" ? "Verify all" : confirm.kind === "verify" ? "Verify" : "Drop"}</button></>}>
          {confirm.kind === "all" && <>This verifies every payment matching the current filters (TZS {n0(s.total_amount)}) and posts their journal entries. Service payments are verified without tax. This can't be undone.</>}
          {confirm.kind === "verify" && <>Payment #{confirm.row.payment_id} from {confirm.row.client_name}: TZS {n0(confirm.row.payment_amount)} by {confirm.row.payment_method}.
            {confirm.row.payment_class === "SERVICE" ? " This is a service payment, so no tax is calculated." : ""} This can't be undone.</>}
          {confirm.kind === "drop" && <>Payment #{confirm.row.payment_id} (TZS {n0(confirm.row.payment_amount)}) will be deleted, its sale lines removed and the stock returned. This can't be undone.</>}
        </Modal>
      )}
    </main>
  );
}
